// The Apps place in the sidebar (operator UX 2026-10-05; DES1 specs "Apps in the sidebar"): the page's words.
// Namespace `apps-nav`; owner: T2-UI1. English is the source; the other 23 languages are DES2's
// (org/work/DES2/handoff/apps-0645-24.json).
// Title, segments, "By {owner}", "Updated {time}", Start in chat and the no-match words are the Library's own
// artifact-panels keys (no duplicates). The Skills-style page, its App ideas and the new-App chat (operator UX
// 2026-10-05) came with all 24. A draft is what the person would type; the Create draft gets its trailing space
// in code (values stay trimmed).
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  appsSearch: 'Search Apps',
  appsDescription:
    'Tools made for how you work. Open, share and keep improving your company’s Apps.',
  appsCreateInChat: 'Create in chat',
  appsEmptyTitle: 'Start with a tool your team needs.',
  appsEmptyBody:
    'Describe it in chat. Your App takes shape beside the conversation and stays private until you share it.',
  appsCreateFirst: 'Create your first App',
  appsCreateDraft: 'Build me an App that',
  appsIdeasTitle: 'Start from an idea',
  appsIdeasIntro: 'Each one opens a new chat with the App described. Change it, then send.',
  appsIdeaPrices: 'Competitor price monitor',
  appsIdeaPricesDraft:
    'Build me an App that monitors competitor prices for my products and shows what changed since last week.',
  appsIdeaStock: 'Stock and reorder planner',
  appsIdeaStockDraft:
    'Build me an App that shows stock per product, flags what runs out within two weeks and suggests reorder quantities.',
  appsIdeaCompare: 'Product comparison',
  appsIdeaCompareDraft:
    'Build me an App that compares products side by side on price, specifications and reviews.',
  appsIdeaReturns: 'Returns tracker',
  appsIdeaReturnsDraft:
    'Build me an App where my team logs returns with photos and follows each refund until it is closed.',
  appsNewHeading: 'An App, made for your work.',
  appsNewIntro: 'Describe what you need. Your App takes shape beside the conversation.',
  appsNewChip: 'Building a new App',
  appsNewChipRemove: 'Stop building a new App',
  appsNewCanvasTitle: 'What should this App do?',
  appsNewCanvasBody: 'Describe it in the chat. Your App takes shape here.',
  appsOpening: 'Opening {app}…',
  appsOpeningUnnamed: 'Opening your App…',
  appsCannotOpen: '{app} can’t open right now',
  appsCannotOpenUnnamed: 'This App can’t open right now',
  appsCannotOpenBody: 'Something stopped it from loading.',
};
export type AppsNavCopyKey = keyof typeof en;

export const appsNavCopy: NamespaceCatalog<AppsNavCopyKey> = {
  en,
  bg: {
    appsSearch: 'Търсене на приложения',
    appsDescription:
      'Инструменти, създадени за начина, по който работите. Отваряйте, споделяйте и подобрявайте приложенията на компанията си.',
    appsCreateInChat: 'Създайте в чата',
    appsEmptyTitle: 'Започнете с инструмент, от който екипът ви се нуждае.',
    appsEmptyBody:
      'Опишете го в чата. Приложението се оформя до разговора и остава лично, докато не го споделите.',
    appsCreateFirst: 'Създайте първото си приложение',
    appsCreateDraft: 'Създай ми приложение, което',
    appsIdeasTitle: 'Започнете от идея',
    appsIdeasIntro: 'Всяка отваря нов чат с описаното приложение. Променете го и го изпратете.',
    appsIdeaPrices: 'Наблюдение на цените на конкурентите',
    appsIdeaPricesDraft:
      'Създай ми приложение, което следи цените на конкурентите за моите продукти и показва какво се е променило от миналата седмица.',
    appsIdeaStock: 'Планиране на наличности и поръчки',
    appsIdeaStockDraft:
      'Създай ми приложение, което показва наличностите по продукт, отбелязва какво ще свърши до две седмици и предлага количества за поръчка.',
    appsIdeaCompare: 'Сравнение на продукти',
    appsIdeaCompareDraft:
      'Създай ми приложение, което сравнява продукти един до друг по цена, характеристики и отзиви.',
    appsIdeaReturns: 'Проследяване на връщания',
    appsIdeaReturnsDraft:
      'Създай ми приложение, в което екипът ми въвежда върнатите стоки със снимки и проследява всяко възстановяване на сума до приключването му.',
    appsNewHeading: 'Приложение, създадено за вашата работа.',
    appsNewIntro: 'Опишете какво ви трябва. Приложението се оформя до разговора.',
    appsNewChip: 'Създаване на ново приложение',
    appsNewChipRemove: 'Спрете създаването на ново приложение',
    appsNewCanvasTitle: 'Какво трябва да прави това приложение?',
    appsNewCanvasBody: 'Опишете го в чата. Приложението се оформя тук.',
    appsOpening: 'Отваряне на {app}…',
    appsOpeningUnnamed: 'Отваряне на приложението…',
    appsCannotOpen: '{app} не може да се отвори в момента',
    appsCannotOpenUnnamed: 'Това приложение не може да се отвори в момента',
    appsCannotOpenBody: 'Нещо попречи на зареждането му.',
  },
  cs: {
    appsSearch: 'Hledat aplikace',
    appsDescription:
      'Nástroje vytvořené pro to, jak pracujete. Otevírejte, sdílejte a vylepšujte aplikace své firmy.',
    appsCreateInChat: 'Vytvořit v chatu',
    appsEmptyTitle: 'Začněte nástrojem, který váš tým potřebuje.',
    appsEmptyBody:
      'Popište ho v chatu. Aplikace vzniká vedle konverzace a zůstane soukromá, dokud ji nesdílíte.',
    appsCreateFirst: 'Vytvořit první aplikaci',
    appsCreateDraft: 'Vytvoř mi aplikaci, která',
    appsIdeasTitle: 'Začněte nápadem',
    appsIdeasIntro: 'Každý otevře nový chat s popsanou aplikací. Upravte ho a odešlete.',
    appsIdeaPrices: 'Sledování cen konkurence',
    appsIdeaPricesDraft:
      'Vytvoř mi aplikaci, která sleduje ceny konkurence u mých produktů a ukazuje, co se změnilo od minulého týdne.',
    appsIdeaStock: 'Plánovač zásob a objednávek',
    appsIdeaStockDraft:
      'Vytvoř mi aplikaci, která ukazuje zásoby podle produktu, označí, co do dvou týdnů dojde, a navrhne množství k objednání.',
    appsIdeaCompare: 'Porovnání produktů',
    appsIdeaCompareDraft:
      'Vytvoř mi aplikaci, která porovná produkty vedle sebe podle ceny, parametrů a recenzí.',
    appsIdeaReturns: 'Evidence vratek',
    appsIdeaReturnsDraft:
      'Vytvoř mi aplikaci, kde můj tým zapisuje vratky s fotkami a sleduje každou refundaci až do vyřízení.',
    appsNewHeading: 'Aplikace vytvořená pro vaši práci.',
    appsNewIntro: 'Popište, co potřebujete. Aplikace vzniká vedle konverzace.',
    appsNewChip: 'Tvorba nové aplikace',
    appsNewChipRemove: 'Ukončit tvorbu nové aplikace',
    appsNewCanvasTitle: 'Co má tato aplikace dělat?',
    appsNewCanvasBody: 'Popište to v chatu. Aplikace vznikne tady.',
    appsOpening: 'Otevírání aplikace {app}…',
    appsOpeningUnnamed: 'Otevírání aplikace…',
    appsCannotOpen: 'Aplikaci {app} teď nelze otevřít',
    appsCannotOpenUnnamed: 'Tuto aplikaci teď nelze otevřít',
    appsCannotOpenBody: 'Něco zabránilo jejímu načtení.',
  },
  da: {
    appsSearch: 'Søg i apps',
    appsDescription:
      'Værktøjer lavet til den måde, I arbejder på. Åbn, del og forbedr virksomhedens apps.',
    appsCreateInChat: 'Opret i chatten',
    appsEmptyTitle: 'Start med et værktøj, dit team har brug for.',
    appsEmptyBody:
      'Beskriv det i chatten. Din app tager form ved siden af samtalen og forbliver privat, indtil du deler den.',
    appsCreateFirst: 'Opret din første app',
    appsCreateDraft: 'Byg en app til mig, der',
    appsIdeasTitle: 'Start fra en idé',
    appsIdeasIntro: 'Hver idé åbner en ny chat med appen beskrevet. Ret den til, og send den.',
    appsIdeaPrices: 'Overvågning af konkurrentpriser',
    appsIdeaPricesDraft:
      'Byg en app til mig, der overvåger konkurrenternes priser på mine produkter og viser, hvad der er ændret siden sidste uge.',
    appsIdeaStock: 'Lager og genbestilling',
    appsIdeaStockDraft:
      'Byg en app til mig, der viser lager pr. produkt, markerer, hvad der slipper op inden for to uger, og foreslår genbestillingsmængder.',
    appsIdeaCompare: 'Produktsammenligning',
    appsIdeaCompareDraft:
      'Byg en app til mig, der sammenligner produkter side om side på pris, specifikationer og anmeldelser.',
    appsIdeaReturns: 'Sporing af returneringer',
    appsIdeaReturnsDraft:
      'Byg en app til mig, hvor mit team registrerer returneringer med billeder og følger hver refusion, til den er afsluttet.',
    appsNewHeading: 'En app, lavet til dit arbejde.',
    appsNewIntro: 'Beskriv, hvad du har brug for. Din app tager form ved siden af samtalen.',
    appsNewChip: 'Bygger en ny app',
    appsNewChipRemove: 'Stop med at bygge en ny app',
    appsNewCanvasTitle: 'Hvad skal denne app gøre?',
    appsNewCanvasBody: 'Beskriv det i chatten. Din app tager form her.',
    appsOpening: 'Åbner {app}…',
    appsOpeningUnnamed: 'Åbner din app…',
    appsCannotOpen: '{app} kan ikke åbnes lige nu',
    appsCannotOpenUnnamed: 'Denne app kan ikke åbnes lige nu',
    appsCannotOpenBody: 'Noget forhindrede den i at indlæse.',
  },
  de: {
    appsSearch: 'Apps durchsuchen',
    appsDescription:
      'Werkzeuge für Ihre Arbeitsweise. Öffnen, teilen und verbessern Sie die Apps Ihres Unternehmens.',
    appsCreateInChat: 'Im Chat erstellen',
    appsEmptyTitle: 'Beginnen Sie mit einem Werkzeug, das Ihr Team braucht.',
    appsEmptyBody:
      'Beschreiben Sie es im Chat. Ihre App entsteht neben dem Gespräch und bleibt privat, bis Sie sie teilen.',
    appsCreateFirst: 'Erste App erstellen',
    appsCreateDraft: 'Erstelle mir eine App, die',
    appsIdeasTitle: 'Mit einer Idee beginnen',
    appsIdeasIntro:
      'Jede öffnet einen neuen Chat mit der beschriebenen App. Passen Sie sie an und senden Sie sie.',
    appsIdeaPrices: 'Preismonitor für Wettbewerber',
    appsIdeaPricesDraft:
      'Erstelle mir eine App, die die Preise der Wettbewerber für meine Produkte beobachtet und zeigt, was sich seit letzter Woche geändert hat.',
    appsIdeaStock: 'Bestands- und Nachbestellplaner',
    appsIdeaStockDraft:
      'Erstelle mir eine App, die den Bestand pro Produkt zeigt, markiert, was innerhalb von zwei Wochen ausgeht, und Nachbestellmengen vorschlägt.',
    appsIdeaCompare: 'Produktvergleich',
    appsIdeaCompareDraft:
      'Erstelle mir eine App, die Produkte nach Preis, technischen Daten und Bewertungen nebeneinander vergleicht.',
    appsIdeaReturns: 'Retourenverfolgung',
    appsIdeaReturnsDraft:
      'Erstelle mir eine App, in der mein Team Retouren mit Fotos erfasst und jede Erstattung bis zum Abschluss verfolgt.',
    appsNewHeading: 'Eine App, gemacht für Ihre Arbeit.',
    appsNewIntro: 'Beschreiben Sie, was Sie brauchen. Ihre App entsteht neben dem Gespräch.',
    appsNewChip: 'Neue App wird erstellt',
    appsNewChipRemove: 'Erstellen der neuen App beenden',
    appsNewCanvasTitle: 'Was soll diese App tun?',
    appsNewCanvasBody: 'Beschreiben Sie es im Chat. Ihre App entsteht hier.',
    appsOpening: '{app} wird geöffnet…',
    appsOpeningUnnamed: 'Ihre App wird geöffnet…',
    appsCannotOpen: '{app} lässt sich gerade nicht öffnen',
    appsCannotOpenUnnamed: 'Diese App lässt sich gerade nicht öffnen',
    appsCannotOpenBody: 'Etwas hat das Laden verhindert.',
  },
  el: {
    appsSearch: 'Αναζήτηση εφαρμογών',
    appsDescription:
      'Εργαλεία φτιαγμένα για τον τρόπο που δουλεύετε. Ανοίξτε, μοιραστείτε και βελτιώστε τις εφαρμογές της εταιρείας σας.',
    appsCreateInChat: 'Δημιουργία στη συνομιλία',
    appsEmptyTitle: 'Ξεκινήστε με ένα εργαλείο που χρειάζεται η ομάδα σας.',
    appsEmptyBody:
      'Περιγράψτε το στη συνομιλία. Η εφαρμογή σας παίρνει μορφή δίπλα στη συζήτηση και μένει ιδιωτική μέχρι να την κοινοποιήσετε.',
    appsCreateFirst: 'Δημιουργήστε την πρώτη σας εφαρμογή',
    appsCreateDraft: 'Φτιάξε μου μια εφαρμογή που',
    appsIdeasTitle: 'Ξεκινήστε από μια ιδέα',
    appsIdeasIntro:
      'Κάθε ιδέα ανοίγει μια νέα συνομιλία με την εφαρμογή περιγραμμένη. Αλλάξτε την και στείλτε την.',
    appsIdeaPrices: 'Παρακολούθηση τιμών ανταγωνιστών',
    appsIdeaPricesDraft:
      'Φτιάξε μου μια εφαρμογή που παρακολουθεί τις τιμές των ανταγωνιστών για τα προϊόντα μου και δείχνει τι άλλαξε από την προηγούμενη εβδομάδα.',
    appsIdeaStock: 'Προγραμματισμός αποθέματος και αναπαραγγελιών',
    appsIdeaStockDraft:
      'Φτιάξε μου μια εφαρμογή που δείχνει το απόθεμα ανά προϊόν, επισημαίνει ό,τι θα τελειώσει μέσα σε δύο εβδομάδες και προτείνει ποσότητες αναπαραγγελίας.',
    appsIdeaCompare: 'Σύγκριση προϊόντων',
    appsIdeaCompareDraft:
      'Φτιάξε μου μια εφαρμογή που συγκρίνει προϊόντα δίπλα δίπλα ως προς την τιμή, τα χαρακτηριστικά και τις κριτικές.',
    appsIdeaReturns: 'Παρακολούθηση επιστροφών',
    appsIdeaReturnsDraft:
      'Φτιάξε μου μια εφαρμογή όπου η ομάδα μου καταγράφει επιστροφές με φωτογραφίες και παρακολουθεί κάθε επιστροφή χρημάτων μέχρι να ολοκληρωθεί.',
    appsNewHeading: 'Μια εφαρμογή, φτιαγμένη για τη δουλειά σας.',
    appsNewIntro: 'Περιγράψτε τι χρειάζεστε. Η εφαρμογή σας παίρνει μορφή δίπλα στη συζήτηση.',
    appsNewChip: 'Δημιουργία νέας εφαρμογής',
    appsNewChipRemove: 'Διακοπή δημιουργίας νέας εφαρμογής',
    appsNewCanvasTitle: 'Τι πρέπει να κάνει αυτή η εφαρμογή;',
    appsNewCanvasBody: 'Περιγράψτε το στη συνομιλία. Η εφαρμογή σας παίρνει μορφή εδώ.',
    appsOpening: 'Άνοιγμα της εφαρμογής {app}…',
    appsOpeningUnnamed: 'Άνοιγμα της εφαρμογής σας…',
    appsCannotOpen: 'Η εφαρμογή {app} δεν μπορεί να ανοίξει αυτή τη στιγμή',
    appsCannotOpenUnnamed: 'Αυτή η εφαρμογή δεν μπορεί να ανοίξει αυτή τη στιγμή',
    appsCannotOpenBody: 'Κάτι εμπόδισε τη φόρτωσή της.',
  },
  es: {
    appsSearch: 'Buscar apps',
    appsDescription:
      'Herramientas hechas para tu forma de trabajar. Abre, comparte y mejora las apps de tu empresa.',
    appsCreateInChat: 'Crear en el chat',
    appsEmptyTitle: 'Empieza con una herramienta que tu equipo necesite.',
    appsEmptyBody:
      'Descríbela en el chat. Tu app toma forma junto a la conversación y sigue siendo privada hasta que la compartas.',
    appsCreateFirst: 'Crear tu primera app',
    appsCreateDraft: 'Créame una app que',
    appsIdeasTitle: 'Empieza con una idea',
    appsIdeasIntro: 'Cada una abre un chat nuevo con la app descrita. Ajústala y envíala.',
    appsIdeaPrices: 'Monitor de precios de la competencia',
    appsIdeaPricesDraft:
      'Créame una app que siga los precios de la competencia para mis productos y muestre qué ha cambiado desde la semana pasada.',
    appsIdeaStock: 'Planificador de stock y reposición',
    appsIdeaStockDraft:
      'Créame una app que muestre el stock por producto, marque lo que se agotará en dos semanas y sugiera cantidades de reposición.',
    appsIdeaCompare: 'Comparador de productos',
    appsIdeaCompareDraft:
      'Créame una app que compare productos lado a lado por precio, especificaciones y reseñas.',
    appsIdeaReturns: 'Seguimiento de devoluciones',
    appsIdeaReturnsDraft:
      'Créame una app donde mi equipo registre devoluciones con fotos y siga cada reembolso hasta cerrarlo.',
    appsNewHeading: 'Una app hecha para tu trabajo.',
    appsNewIntro: 'Describe lo que necesitas. Tu app toma forma junto a la conversación.',
    appsNewChip: 'Creando una app nueva',
    appsNewChipRemove: 'Dejar de crear una app nueva',
    appsNewCanvasTitle: '¿Qué debe hacer esta app?',
    appsNewCanvasBody: 'Descríbelo en el chat. Tu app toma forma aquí.',
    appsOpening: 'Abriendo {app}…',
    appsOpeningUnnamed: 'Abriendo tu app…',
    appsCannotOpen: '{app} no se puede abrir ahora',
    appsCannotOpenUnnamed: 'Esta app no se puede abrir ahora',
    appsCannotOpenBody: 'Algo impidió que se cargara.',
  },
  et: {
    appsSearch: 'Otsi rakendusi',
    appsDescription:
      'Tööriistad, mis on loodud sinu töö jaoks. Ava, jaga ja täiusta oma ettevõtte rakendusi.',
    appsCreateInChat: 'Loo vestluses',
    appsEmptyTitle: 'Alusta tööriistast, mida su meeskond vajab.',
    appsEmptyBody:
      'Kirjelda seda vestluses. Rakendus võtab kuju vestluse kõrval ja jääb privaatseks, kuni sa seda jagad.',
    appsCreateFirst: 'Loo oma esimene rakendus',
    appsCreateDraft: 'Tee mulle rakendus, mis',
    appsIdeasTitle: 'Alusta ideest',
    appsIdeasIntro: 'Iga idee avab uue vestluse kirjeldatud rakendusega. Muuda seda ja saada.',
    appsIdeaPrices: 'Konkurentide hindade jälgija',
    appsIdeaPricesDraft:
      'Tee mulle rakendus, mis jälgib konkurentide hindu minu toodetele ja näitab, mis on eelmisest nädalast muutunud.',
    appsIdeaStock: 'Laoseisu ja tellimuste planeerija',
    appsIdeaStockDraft:
      'Tee mulle rakendus, mis näitab laoseisu toote kaupa, märgib, mis kahe nädala jooksul otsa saab, ja soovitab tellimiskoguseid.',
    appsIdeaCompare: 'Toodete võrdlus',
    appsIdeaCompareDraft:
      'Tee mulle rakendus, mis võrdleb tooteid kõrvuti hinna, tehniliste andmete ja arvustuste järgi.',
    appsIdeaReturns: 'Tagastuste jälgija',
    appsIdeaReturnsDraft:
      'Tee mulle rakendus, kus mu meeskond registreerib tagastused koos fotodega ja jälgib iga raha tagastamist lõpuni.',
    appsNewHeading: 'Rakendus, mis on tehtud sinu töö jaoks.',
    appsNewIntro: 'Kirjelda, mida vajad. Rakendus võtab kuju vestluse kõrval.',
    appsNewChip: 'Uue rakenduse loomine',
    appsNewChipRemove: 'Lõpeta uue rakenduse loomine',
    appsNewCanvasTitle: 'Mida see rakendus tegema peaks?',
    appsNewCanvasBody: 'Kirjelda seda vestluses. Rakendus võtab kuju siin.',
    appsOpening: 'Avan rakendust {app}…',
    appsOpeningUnnamed: 'Avan rakendust…',
    appsCannotOpen: 'Rakendust {app} ei saa praegu avada',
    appsCannotOpenUnnamed: 'Seda rakendust ei saa praegu avada',
    appsCannotOpenBody: 'Miski takistas selle laadimist.',
  },
  fi: {
    appsSearch: 'Hae sovelluksia',
    appsDescription:
      'Työkaluja juuri teidän tapaanne tehdä työtä. Avaa, jaa ja paranna yrityksesi sovelluksia.',
    appsCreateInChat: 'Luo chatissa',
    appsEmptyTitle: 'Aloita työkalusta, jota tiimisi tarvitsee.',
    appsEmptyBody:
      'Kuvaile se chatissa. Sovelluksesi muotoutuu keskustelun vierellä ja pysyy yksityisenä, kunnes jaat sen.',
    appsCreateFirst: 'Luo ensimmäinen sovelluksesi',
    appsCreateDraft: 'Tee minulle sovellus, joka',
    appsIdeasTitle: 'Aloita ideasta',
    appsIdeasIntro:
      'Jokainen avaa uuden chatin, jossa sovellus on kuvattu. Muokkaa sitä ja lähetä.',
    appsIdeaPrices: 'Kilpailijoiden hintaseuranta',
    appsIdeaPricesDraft:
      'Tee minulle sovellus, joka seuraa kilpailijoiden hintoja tuotteilleni ja näyttää, mikä on muuttunut viime viikon jälkeen.',
    appsIdeaStock: 'Varasto- ja tilaussuunnittelija',
    appsIdeaStockDraft:
      'Tee minulle sovellus, joka näyttää varastosaldon tuotteittain, merkitsee, mikä loppuu kahden viikon kuluessa, ja ehdottaa tilausmääriä.',
    appsIdeaCompare: 'Tuotevertailu',
    appsIdeaCompareDraft:
      'Tee minulle sovellus, joka vertailee tuotteita rinnakkain hinnan, teknisten tietojen ja arvostelujen perusteella.',
    appsIdeaReturns: 'Palautusten seuranta',
    appsIdeaReturnsDraft:
      'Tee minulle sovellus, johon tiimini kirjaa palautukset kuvineen ja jossa jokaista hyvitystä seurataan loppuun asti.',
    appsNewHeading: 'Sovellus, joka on tehty sinun työhösi.',
    appsNewIntro: 'Kuvaile, mitä tarvitset. Sovelluksesi muotoutuu keskustelun vierellä.',
    appsNewChip: 'Uuden sovelluksen luonti',
    appsNewChipRemove: 'Lopeta uuden sovelluksen luonti',
    appsNewCanvasTitle: 'Mitä tämän sovelluksen pitäisi tehdä?',
    appsNewCanvasBody: 'Kuvaile se chatissa. Sovelluksesi muotoutuu tähän.',
    appsOpening: 'Avataan sovellusta {app}…',
    appsOpeningUnnamed: 'Avataan sovellustasi…',
    appsCannotOpen: 'Sovellusta {app} ei voi avata juuri nyt',
    appsCannotOpenUnnamed: 'Tätä sovellusta ei voi avata juuri nyt',
    appsCannotOpenBody: 'Jokin esti sen latautumisen.',
  },
  fr: {
    appsSearch: 'Rechercher des apps',
    appsDescription:
      'Des outils conçus pour votre façon de travailler. Ouvrez, partagez et améliorez les apps de votre entreprise.',
    appsCreateInChat: 'Créer dans le chat',
    appsEmptyTitle: 'Commencez par un outil dont votre équipe a besoin.',
    appsEmptyBody:
      'Décrivez-le dans le chat. Votre app prend forme à côté de la conversation et reste privée jusqu’à ce que vous la partagiez.',
    appsCreateFirst: 'Créer votre première app',
    appsCreateDraft: 'Créez-moi une app qui',
    appsIdeasTitle: 'Partir d’une idée',
    appsIdeasIntro:
      'Chacune ouvre un nouveau chat avec l’app décrite. Modifiez-la, puis envoyez-la.',
    appsIdeaPrices: 'Veille des prix concurrents',
    appsIdeaPricesDraft:
      'Créez-moi une app qui surveille les prix des concurrents pour mes produits et montre ce qui a changé depuis la semaine dernière.',
    appsIdeaStock: 'Planificateur de stock et de réassort',
    appsIdeaStockDraft:
      'Créez-moi une app qui affiche le stock par produit, signale ce qui sera épuisé d’ici deux semaines et propose des quantités de réassort.',
    appsIdeaCompare: 'Comparateur de produits',
    appsIdeaCompareDraft:
      'Créez-moi une app qui compare des produits côte à côte selon le prix, les caractéristiques et les avis.',
    appsIdeaReturns: 'Suivi des retours',
    appsIdeaReturnsDraft:
      'Créez-moi une app où mon équipe enregistre les retours avec photos et suit chaque remboursement jusqu’à sa clôture.',
    appsNewHeading: 'Une app, faite pour votre travail.',
    appsNewIntro:
      'Décrivez ce dont vous avez besoin. Votre app prend forme à côté de la conversation.',
    appsNewChip: 'Création d’une nouvelle app',
    appsNewChipRemove: 'Arrêter la création d’une nouvelle app',
    appsNewCanvasTitle: 'Que doit faire cette app ?',
    appsNewCanvasBody: 'Décrivez-le dans le chat. Votre app prend forme ici.',
    appsOpening: 'Ouverture de {app}…',
    appsOpeningUnnamed: 'Ouverture de votre app…',
    appsCannotOpen: '{app} ne peut pas s’ouvrir pour le moment',
    appsCannotOpenUnnamed: 'Cette app ne peut pas s’ouvrir pour le moment',
    appsCannotOpenBody: 'Quelque chose a empêché son chargement.',
  },
  ga: {
    appsSearch: 'Cuardaigh aipeanna',
    appsDescription:
      'Uirlisí a rinneadh don chaoi a n-oibríonn tú. Oscail, roinn agus feabhsaigh aipeanna do chomhlachta.',
    appsCreateInChat: 'Cruthaigh sa chomhrá',
    appsEmptyTitle: 'Tosaigh le huirlis a theastaíonn ó d’fhoireann.',
    appsEmptyBody:
      'Déan cur síos air sa chomhrá. Cuirtear cruth ar d’aip in aice leis an gcomhrá agus fanann sí príobháideach go dtí go roinneann tú í.',
    appsCreateFirst: 'Cruthaigh do chéad aip',
    appsCreateDraft: 'Déan aip dom a',
    appsIdeasTitle: 'Tosaigh ó smaoineamh',
    appsIdeasIntro:
      'Osclaíonn gach ceann comhrá nua leis an aip curtha síos. Athraigh í, ansin seol í.',
    appsIdeaPrices: 'Monatóir praghsanna iomaitheoirí',
    appsIdeaPricesDraft:
      'Déan aip dom a choimeádann súil ar phraghsanna iomaitheoirí do mo tháirgí agus a thaispeánann cad a d’athraigh ón tseachtain seo caite.',
    appsIdeaStock: 'Pleanálaí stoic agus athordaithe',
    appsIdeaStockDraft:
      'Déan aip dom a thaispeánann an stoc de réir táirge, a mharcálann cad a rithfidh amach laistigh de choicís agus a mholann cainníochtaí athordaithe.',
    appsIdeaCompare: 'Comparáid táirgí',
    appsIdeaCompareDraft:
      'Déan aip dom a chuireann táirgí i gcomparáid taobh le taobh de réir praghais, sonraíochtaí agus léirmheasanna.',
    appsIdeaReturns: 'Rianú earraí ar ais',
    appsIdeaReturnsDraft:
      'Déan aip dom ina gcláraíonn m’fhoireann earraí a cuireadh ar ais le grianghraif agus ina leanann sí gach aisíocaíocht go dtí go ndúntar í.',
    appsNewHeading: 'Aip, déanta do d’obair.',
    appsNewIntro:
      'Déan cur síos ar a bhfuil uait. Cuirtear cruth ar d’aip in aice leis an gcomhrá.',
    appsNewChip: 'Aip nua á tógáil',
    appsNewChipRemove: 'Stop ag tógáil aip nua',
    appsNewCanvasTitle: 'Cad ba cheart don aip seo a dhéanamh?',
    appsNewCanvasBody: 'Déan cur síos air sa chomhrá. Cuirtear cruth ar d’aip anseo.',
    appsOpening: '{app} á oscailt…',
    appsOpeningUnnamed: 'D’aip á hoscailt…',
    appsCannotOpen: 'Ní féidir {app} a oscailt faoi láthair',
    appsCannotOpenUnnamed: 'Ní féidir an aip seo a oscailt faoi láthair',
    appsCannotOpenBody: 'Chuir rud éigin bac ar a lódáil.',
  },
  hr: {
    appsSearch: 'Pretraži aplikacije',
    appsDescription:
      'Alati stvoreni za način na koji radite. Otvarajte, dijelite i poboljšavajte aplikacije svoje tvrtke.',
    appsCreateInChat: 'Izradite u chatu',
    appsEmptyTitle: 'Započnite alatom koji vašem timu treba.',
    appsEmptyBody:
      'Opišite ga u chatu. Vaša aplikacija nastaje uz razgovor i ostaje privatna dok je ne podijelite.',
    appsCreateFirst: 'Izradite svoju prvu aplikaciju',
    appsCreateDraft: 'Izradi mi aplikaciju koja',
    appsIdeasTitle: 'Započnite od ideje',
    appsIdeasIntro: 'Svaka otvara novi chat s opisanom aplikacijom. Prilagodite je i pošaljite.',
    appsIdeaPrices: 'Praćenje cijena konkurencije',
    appsIdeaPricesDraft:
      'Izradi mi aplikaciju koja prati cijene konkurencije za moje proizvode i pokazuje što se promijenilo od prošlog tjedna.',
    appsIdeaStock: 'Planer zaliha i narudžbi',
    appsIdeaStockDraft:
      'Izradi mi aplikaciju koja prikazuje zalihe po proizvodu, označava što će nestati u roku od dva tjedna i predlaže količine za narudžbu.',
    appsIdeaCompare: 'Usporedba proizvoda',
    appsIdeaCompareDraft:
      'Izradi mi aplikaciju koja uspoređuje proizvode jedan uz drugi po cijeni, specifikacijama i recenzijama.',
    appsIdeaReturns: 'Praćenje povrata',
    appsIdeaReturnsDraft:
      'Izradi mi aplikaciju u kojoj moj tim bilježi povrate s fotografijama i prati svaki povrat novca do zatvaranja.',
    appsNewHeading: 'Aplikacija stvorena za vaš posao.',
    appsNewIntro: 'Opišite što vam treba. Vaša aplikacija nastaje uz razgovor.',
    appsNewChip: 'Izrada nove aplikacije',
    appsNewChipRemove: 'Prekini izradu nove aplikacije',
    appsNewCanvasTitle: 'Što bi ova aplikacija trebala raditi?',
    appsNewCanvasBody: 'Opišite to u chatu. Vaša aplikacija nastaje ovdje.',
    appsOpening: 'Otvaranje aplikacije {app}…',
    appsOpeningUnnamed: 'Otvaranje vaše aplikacije…',
    appsCannotOpen: '{app} trenutačno se ne može otvoriti',
    appsCannotOpenUnnamed: 'Ova se aplikacija trenutačno ne može otvoriti',
    appsCannotOpenBody: 'Nešto je spriječilo njezino učitavanje.',
  },
  hu: {
    appsSearch: 'Alkalmazások keresése',
    appsDescription:
      'Az Ön munkamódjára szabott eszközök. Nyissa meg, ossza meg és fejlessze a vállalata alkalmazásait.',
    appsCreateInChat: 'Létrehozás a csevegésben',
    appsEmptyTitle: 'Kezdjen egy eszközzel, amelyre a csapatának szüksége van.',
    appsEmptyBody:
      'Írja le a csevegésben. Az alkalmazás a beszélgetés mellett formálódik, és privát marad, amíg meg nem osztja.',
    appsCreateFirst: 'Első alkalmazás létrehozása',
    appsCreateDraft: 'Készíts nekem egy alkalmazást, amely',
    appsIdeasTitle: 'Kezdés egy ötletből',
    appsIdeasIntro: 'Mindegyik új csevegést nyit a leírt alkalmazással. Módosítsa, majd küldje el.',
    appsIdeaPrices: 'Versenytársak árainak figyelése',
    appsIdeaPricesDraft:
      'Készíts nekem egy alkalmazást, amely figyeli a versenytársak árait a termékeimre, és megmutatja, mi változott a múlt hét óta.',
    appsIdeaStock: 'Készlet- és utánrendelés-tervező',
    appsIdeaStockDraft:
      'Készíts nekem egy alkalmazást, amely termékenként mutatja a készletet, jelzi, mi fogy el két héten belül, és utánrendelési mennyiségeket javasol.',
    appsIdeaCompare: 'Termék-összehasonlítás',
    appsIdeaCompareDraft:
      'Készíts nekem egy alkalmazást, amely egymás mellett hasonlítja össze a termékeket ár, műszaki adatok és vélemények alapján.',
    appsIdeaReturns: 'Visszáru-követés',
    appsIdeaReturnsDraft:
      'Készíts nekem egy alkalmazást, amelyben a csapatom fényképekkel rögzíti a visszárukat, és minden visszatérítést lezárásig követ.',
    appsNewHeading: 'Egy alkalmazás, az Ön munkájára szabva.',
    appsNewIntro: 'Írja le, mire van szüksége. Az alkalmazás a beszélgetés mellett formálódik.',
    appsNewChip: 'Új alkalmazás készítése',
    appsNewChipRemove: 'Az új alkalmazás készítésének leállítása',
    appsNewCanvasTitle: 'Mit csináljon ez az alkalmazás?',
    appsNewCanvasBody: 'Írja le a csevegésben. Az alkalmazás itt formálódik.',
    appsOpening: '{app} megnyitása…',
    appsOpeningUnnamed: 'Az alkalmazás megnyitása…',
    appsCannotOpen: 'A(z) {app} most nem nyitható meg',
    appsCannotOpenUnnamed: 'Ez az alkalmazás most nem nyitható meg',
    appsCannotOpenBody: 'Valami megakadályozta a betöltését.',
  },
  it: {
    appsSearch: 'Cerca App',
    appsDescription:
      'Strumenti pensati per il tuo modo di lavorare. Apri, condividi e migliora le App della tua azienda.',
    appsCreateInChat: 'Crea in chat',
    appsEmptyTitle: 'Inizia con uno strumento che serve al tuo team.',
    appsEmptyBody:
      'Descrivilo in chat. La tua App prende forma accanto alla conversazione e resta privata finché non la condividi.',
    appsCreateFirst: 'Crea la tua prima App',
    appsCreateDraft: 'Creami un’App che',
    appsIdeasTitle: 'Parti da un’idea',
    appsIdeasIntro: 'Ognuna apre una nuova chat con l’App descritta. Modificala, poi inviala.',
    appsIdeaPrices: 'Monitor prezzi dei concorrenti',
    appsIdeaPricesDraft:
      'Creami un’App che monitori i prezzi dei concorrenti per i miei prodotti e mostri cosa è cambiato dalla settimana scorsa.',
    appsIdeaStock: 'Pianificatore di scorte e riordini',
    appsIdeaStockDraft:
      'Creami un’App che mostri le scorte per prodotto, segnali cosa finirà entro due settimane e suggerisca le quantità da riordinare.',
    appsIdeaCompare: 'Confronto prodotti',
    appsIdeaCompareDraft:
      'Creami un’App che confronti i prodotti fianco a fianco per prezzo, specifiche e recensioni.',
    appsIdeaReturns: 'Gestione resi',
    appsIdeaReturnsDraft:
      'Creami un’App in cui il mio team registra i resi con foto e segue ogni rimborso fino alla chiusura.',
    appsNewHeading: 'Un’App fatta per il tuo lavoro.',
    appsNewIntro: 'Descrivi ciò che ti serve. La tua App prende forma accanto alla conversazione.',
    appsNewChip: 'Creazione di una nuova App',
    appsNewChipRemove: 'Interrompi la creazione di una nuova App',
    appsNewCanvasTitle: 'Cosa deve fare questa App?',
    appsNewCanvasBody: 'Descrivilo in chat. La tua App prende forma qui.',
    appsOpening: 'Apertura di {app}…',
    appsOpeningUnnamed: 'Apertura della tua App…',
    appsCannotOpen: 'Impossibile aprire {app} in questo momento',
    appsCannotOpenUnnamed: 'Impossibile aprire questa App in questo momento',
    appsCannotOpenBody: 'Qualcosa ne ha impedito il caricamento.',
  },
  lt: {
    appsSearch: 'Ieškoti programėlių',
    appsDescription:
      'Įrankiai, sukurti jūsų darbo būdui. Atidarykite, bendrinkite ir tobulinkite savo įmonės programėles.',
    appsCreateInChat: 'Kurti pokalbyje',
    appsEmptyTitle: 'Pradėkite nuo įrankio, kurio reikia jūsų komandai.',
    appsEmptyBody:
      'Aprašykite jį pokalbyje. Programėlė kuriama šalia pokalbio ir lieka privati, kol ja nepasidalinsite.',
    appsCreateFirst: 'Sukurti pirmąją programėlę',
    appsCreateDraft: 'Sukurk man programėlę, kuri',
    appsIdeasTitle: 'Pradėkite nuo idėjos',
    appsIdeasIntro:
      'Kiekviena atidaro naują pokalbį su aprašyta programėle. Pakeiskite ją ir išsiųskite.',
    appsIdeaPrices: 'Konkurentų kainų stebėjimas',
    appsIdeaPricesDraft:
      'Sukurk man programėlę, kuri stebėtų konkurentų kainas mano produktams ir rodytų, kas pasikeitė nuo praėjusios savaitės.',
    appsIdeaStock: 'Atsargų ir užsakymų planuoklis',
    appsIdeaStockDraft:
      'Sukurk man programėlę, kuri rodytų atsargas pagal produktą, pažymėtų, kas baigsis per dvi savaites, ir siūlytų užsakymo kiekius.',
    appsIdeaCompare: 'Produktų palyginimas',
    appsIdeaCompareDraft:
      'Sukurk man programėlę, kuri palygintų produktus greta pagal kainą, specifikacijas ir atsiliepimus.',
    appsIdeaReturns: 'Grąžinimų sekimas',
    appsIdeaReturnsDraft:
      'Sukurk man programėlę, kurioje mano komanda registruotų grąžinimus su nuotraukomis ir sektų kiekvieną pinigų grąžinimą iki pabaigos.',
    appsNewHeading: 'Programėlė, sukurta jūsų darbui.',
    appsNewIntro: 'Aprašykite, ko jums reikia. Programėlė kuriama šalia pokalbio.',
    appsNewChip: 'Kuriama nauja programėlė',
    appsNewChipRemove: 'Nutraukti naujos programėlės kūrimą',
    appsNewCanvasTitle: 'Ką turėtų daryti ši programėlė?',
    appsNewCanvasBody: 'Aprašykite tai pokalbyje. Programėlė atsiras čia.',
    appsOpening: 'Atidaroma {app}…',
    appsOpeningUnnamed: 'Atidaroma programėlė…',
    appsCannotOpen: '{app} šiuo metu negalima atidaryti',
    appsCannotOpenUnnamed: 'Šios programėlės šiuo metu negalima atidaryti',
    appsCannotOpenBody: 'Kažkas sutrukdė ją įkelti.',
  },
  lv: {
    appsSearch: 'Meklēt lietotnes',
    appsDescription:
      'Rīki, kas veidoti jūsu darba veidam. Atveriet, kopīgojiet un uzlabojiet sava uzņēmuma lietotnes.',
    appsCreateInChat: 'Izveidot tērzēšanā',
    appsEmptyTitle: 'Sāciet ar rīku, kas vajadzīgs jūsu komandai.',
    appsEmptyBody:
      'Aprakstiet to tērzēšanā. Lietotne veidojas blakus sarunai un paliek privāta, līdz jūs to kopīgojat.',
    appsCreateFirst: 'Izveidot pirmo lietotni',
    appsCreateDraft: 'Izveido man lietotni, kas',
    appsIdeasTitle: 'Sāciet ar ideju',
    appsIdeasIntro:
      'Katra atver jaunu tērzēšanu ar aprakstīto lietotni. Pielāgojiet to un nosūtiet.',
    appsIdeaPrices: 'Konkurentu cenu uzraudzība',
    appsIdeaPricesDraft:
      'Izveido man lietotni, kas seko konkurentu cenām maniem produktiem un parāda, kas mainījies kopš pagājušās nedēļas.',
    appsIdeaStock: 'Krājumu un pasūtījumu plānotājs',
    appsIdeaStockDraft:
      'Izveido man lietotni, kas rāda krājumus pa produktiem, atzīmē, kas beigsies divu nedēļu laikā, un iesaka pasūtāmos daudzumus.',
    appsIdeaCompare: 'Produktu salīdzinājums',
    appsIdeaCompareDraft:
      'Izveido man lietotni, kas salīdzina produktus blakus pēc cenas, specifikācijām un atsauksmēm.',
    appsIdeaReturns: 'Atgriezto preču uzskaite',
    appsIdeaReturnsDraft:
      'Izveido man lietotni, kurā mana komanda reģistrē atgrieztās preces ar fotoattēliem un seko katrai naudas atmaksai līdz tās noslēgšanai.',
    appsNewHeading: 'Lietotne, kas veidota jūsu darbam.',
    appsNewIntro: 'Aprakstiet, kas jums vajadzīgs. Lietotne veidojas blakus sarunai.',
    appsNewChip: 'Jaunas lietotnes veidošana',
    appsNewChipRemove: 'Pārtraukt jaunas lietotnes veidošanu',
    appsNewCanvasTitle: 'Ko šai lietotnei vajadzētu darīt?',
    appsNewCanvasBody: 'Aprakstiet to tērzēšanā. Lietotne veidojas šeit.',
    appsOpening: 'Atver {app}…',
    appsOpeningUnnamed: 'Atver jūsu lietotni…',
    appsCannotOpen: '{app} pašlaik nevar atvērt',
    appsCannotOpenUnnamed: 'Šo lietotni pašlaik nevar atvērt',
    appsCannotOpenBody: 'Kaut kas traucēja to ielādēt.',
  },
  mt: {
    appsSearch: 'Fittex l-apps',
    appsDescription:
      'Għodod magħmula għall-mod kif taħdem. Iftaħ, aqsam u tejjeb l-apps tal-kumpanija tiegħek.',
    appsCreateInChat: 'Oħloq fiċ-chat',
    appsEmptyTitle: 'Ibda b’għodda li jeħtieġ it-tim tiegħek.',
    appsEmptyBody:
      'Iddeskriviha fiċ-chat. L-app tiegħek tieħu l-forma ħdejn il-konverżazzjoni u tibqa’ privata sakemm taqsamha.',
    appsCreateFirst: 'Oħloq l-ewwel app tiegħek',
    appsCreateDraft: 'Ibnili app li',
    appsIdeasTitle: 'Ibda minn idea',
    appsIdeasIntro: 'Kull waħda tiftaħ chat ġdid bl-app deskritta. Ibdilha, imbagħad ibgħatha.',
    appsIdeaPrices: 'Monitoraġġ tal-prezzijiet tal-kompetituri',
    appsIdeaPricesDraft:
      'Ibnili app li ssegwi l-prezzijiet tal-kompetituri għall-prodotti tiegħi u turi x’inbidel mill-ġimgħa l-oħra.',
    appsIdeaStock: 'Pjanifikatur tal-istokk u l-ordnijiet',
    appsIdeaStockDraft:
      'Ibnili app li turi l-istokk għal kull prodott, timmarka dak li se jispiċċa fi żmien ġimagħtejn u tissuġġerixxi kwantitajiet biex nerġa’ nordna.',
    appsIdeaCompare: 'Tqabbil tal-prodotti',
    appsIdeaCompareDraft:
      'Ibnili app li tqabbel il-prodotti ħdejn xulxin skont il-prezz, l-ispeċifikazzjonijiet u r-reviżjonijiet.',
    appsIdeaReturns: 'Traċċar tar-ritorni',
    appsIdeaReturnsDraft:
      'Ibnili app fejn it-tim tiegħi jirreġistra r-ritorni b’ritratti u jsegwi kull rifużjoni sakemm tingħalaq.',
    appsNewHeading: 'App, magħmula għax-xogħol tiegħek.',
    appsNewIntro:
      'Iddeskrivi dak li għandek bżonn. L-app tiegħek tieħu l-forma ħdejn il-konverżazzjoni.',
    appsNewChip: 'Qed tinbena app ġdida',
    appsNewChipRemove: 'Waqqaf il-bini ta’ app ġdida',
    appsNewCanvasTitle: 'X’għandha tagħmel din l-app?',
    appsNewCanvasBody: 'Iddeskriviha fiċ-chat. L-app tiegħek tieħu l-forma hawn.',
    appsOpening: 'Qed tinfetaħ {app}…',
    appsOpeningUnnamed: 'Qed tinfetaħ l-app tiegħek…',
    appsCannotOpen: '{app} ma tistax tinfetaħ bħalissa',
    appsCannotOpenUnnamed: 'Din l-app ma tistax tinfetaħ bħalissa',
    appsCannotOpenBody: 'Xi ħaġa żammitha milli titgħabba.',
  },
  nl: {
    appsSearch: 'Apps zoeken',
    appsDescription:
      'Tools gemaakt voor de manier waarop je werkt. Open, deel en verbeter de apps van je bedrijf.',
    appsCreateInChat: 'Maak in de chat',
    appsEmptyTitle: 'Begin met een tool die je team nodig heeft.',
    appsEmptyBody:
      'Beschrijf het in de chat. Je app krijgt vorm naast het gesprek en blijft privé tot je hem deelt.',
    appsCreateFirst: 'Maak je eerste app',
    appsCreateDraft: 'Bouw een app voor me die',
    appsIdeasTitle: 'Begin met een idee',
    appsIdeasIntro:
      'Elk idee opent een nieuwe chat met de app beschreven. Pas hem aan en verstuur hem.',
    appsIdeaPrices: 'Prijsmonitor voor concurrenten',
    appsIdeaPricesDraft:
      'Bouw een app voor me die de prijzen van concurrenten voor mijn producten volgt en laat zien wat er sinds vorige week is veranderd.',
    appsIdeaStock: 'Voorraad- en bestelplanner',
    appsIdeaStockDraft:
      'Bouw een app voor me die de voorraad per product toont, markeert wat binnen twee weken op is en bestelhoeveelheden voorstelt.',
    appsIdeaCompare: 'Productvergelijking',
    appsIdeaCompareDraft:
      'Bouw een app voor me die producten naast elkaar vergelijkt op prijs, specificaties en reviews.',
    appsIdeaReturns: 'Retourtracker',
    appsIdeaReturnsDraft:
      'Bouw een app voor me waarin mijn team retouren met foto’s vastlegt en elke terugbetaling volgt tot die is afgerond.',
    appsNewHeading: 'Een app, gemaakt voor jouw werk.',
    appsNewIntro: 'Beschrijf wat je nodig hebt. Je app krijgt vorm naast het gesprek.',
    appsNewChip: 'Nieuwe app bouwen',
    appsNewChipRemove: 'Stoppen met een nieuwe app bouwen',
    appsNewCanvasTitle: 'Wat moet deze app doen?',
    appsNewCanvasBody: 'Beschrijf het in de chat. Je app krijgt hier vorm.',
    appsOpening: '{app} wordt geopend…',
    appsOpeningUnnamed: 'Je app wordt geopend…',
    appsCannotOpen: '{app} kan nu niet worden geopend',
    appsCannotOpenUnnamed: 'Deze app kan nu niet worden geopend',
    appsCannotOpenBody: 'Iets hield het laden tegen.',
  },
  pl: {
    appsSearch: 'Szukaj aplikacji',
    appsDescription:
      'Narzędzia stworzone pod twój sposób pracy. Otwieraj, udostępniaj i ulepszaj aplikacje swojej firmy.',
    appsCreateInChat: 'Utwórz na czacie',
    appsEmptyTitle: 'Zacznij od narzędzia, którego potrzebuje twój zespół.',
    appsEmptyBody:
      'Opisz je na czacie. Aplikacja powstaje obok rozmowy i pozostaje prywatna, dopóki jej nie udostępnisz.',
    appsCreateFirst: 'Utwórz pierwszą aplikację',
    appsCreateDraft: 'Zbuduj mi aplikację, która',
    appsIdeasTitle: 'Zacznij od pomysłu',
    appsIdeasIntro: 'Każdy otwiera nowy czat z opisaną aplikacją. Zmień go i wyślij.',
    appsIdeaPrices: 'Monitor cen konkurencji',
    appsIdeaPricesDraft:
      'Zbuduj mi aplikację, która śledzi ceny konkurencji dla moich produktów i pokazuje, co zmieniło się od zeszłego tygodnia.',
    appsIdeaStock: 'Planer zapasów i zamówień',
    appsIdeaStockDraft:
      'Zbuduj mi aplikację, która pokazuje stan magazynu dla każdego produktu, oznacza, czego zabraknie w ciągu dwóch tygodni, i proponuje ilości do zamówienia.',
    appsIdeaCompare: 'Porównanie produktów',
    appsIdeaCompareDraft:
      'Zbuduj mi aplikację, która porównuje produkty obok siebie pod względem ceny, specyfikacji i opinii.',
    appsIdeaReturns: 'Obsługa zwrotów',
    appsIdeaReturnsDraft:
      'Zbuduj mi aplikację, w której mój zespół rejestruje zwroty ze zdjęciami i śledzi każdy zwrot pieniędzy aż do zamknięcia.',
    appsNewHeading: 'Aplikacja stworzona dla twojej pracy.',
    appsNewIntro: 'Opisz, czego potrzebujesz. Aplikacja powstaje obok rozmowy.',
    appsNewChip: 'Tworzenie nowej aplikacji',
    appsNewChipRemove: 'Przerwij tworzenie nowej aplikacji',
    appsNewCanvasTitle: 'Co ma robić ta aplikacja?',
    appsNewCanvasBody: 'Opisz to na czacie. Aplikacja powstanie tutaj.',
    appsOpening: 'Otwieranie aplikacji {app}…',
    appsOpeningUnnamed: 'Otwieranie aplikacji…',
    appsCannotOpen: 'Nie można teraz otworzyć aplikacji {app}',
    appsCannotOpenUnnamed: 'Nie można teraz otworzyć tej aplikacji',
    appsCannotOpenBody: 'Coś uniemożliwiło jej wczytanie.',
  },
  pt: {
    appsSearch: 'Pesquisar Apps',
    appsDescription:
      'Ferramentas feitas para a sua forma de trabalhar. Abra, partilhe e melhore as Apps da sua empresa.',
    appsCreateInChat: 'Criar no chat',
    appsEmptyTitle: 'Comece com uma ferramenta de que a sua equipa precisa.',
    appsEmptyBody:
      'Descreva-a no chat. A sua App ganha forma ao lado da conversa e fica privada até a partilhar.',
    appsCreateFirst: 'Criar a sua primeira App',
    appsCreateDraft: 'Crie-me uma App que',
    appsIdeasTitle: 'Comece por uma ideia',
    appsIdeasIntro: 'Cada uma abre um novo chat com a App descrita. Ajuste-a e envie.',
    appsIdeaPrices: 'Monitor de preços da concorrência',
    appsIdeaPricesDraft:
      'Crie-me uma App que acompanhe os preços da concorrência para os meus produtos e mostre o que mudou desde a semana passada.',
    appsIdeaStock: 'Planeador de stock e reposição',
    appsIdeaStockDraft:
      'Crie-me uma App que mostre o stock por produto, assinale o que vai esgotar dentro de duas semanas e sugira quantidades de reposição.',
    appsIdeaCompare: 'Comparação de produtos',
    appsIdeaCompareDraft:
      'Crie-me uma App que compare produtos lado a lado por preço, especificações e avaliações.',
    appsIdeaReturns: 'Gestão de devoluções',
    appsIdeaReturnsDraft:
      'Crie-me uma App onde a minha equipa regista devoluções com fotografias e acompanha cada reembolso até ser concluído.',
    appsNewHeading: 'Uma App, feita para o seu trabalho.',
    appsNewIntro: 'Descreva o que precisa. A sua App ganha forma ao lado da conversa.',
    appsNewChip: 'A criar uma nova App',
    appsNewChipRemove: 'Parar de criar uma nova App',
    appsNewCanvasTitle: 'O que deve fazer esta App?',
    appsNewCanvasBody: 'Descreva-o no chat. A sua App ganha forma aqui.',
    appsOpening: 'A abrir {app}…',
    appsOpeningUnnamed: 'A abrir a sua App…',
    appsCannotOpen: 'Não é possível abrir {app} neste momento',
    appsCannotOpenUnnamed: 'Não é possível abrir esta App neste momento',
    appsCannotOpenBody: 'Algo impediu o carregamento.',
  },
  ro: {
    appsSearch: 'Caută aplicații',
    appsDescription:
      'Instrumente create pentru felul în care lucrați. Deschideți, partajați și îmbunătățiți aplicațiile companiei.',
    appsCreateInChat: 'Creează în chat',
    appsEmptyTitle: 'Începeți cu un instrument de care are nevoie echipa.',
    appsEmptyBody:
      'Descrieți-l în chat. Aplicația prinde contur lângă conversație și rămâne privată până o partajați.',
    appsCreateFirst: 'Creează prima aplicație',
    appsCreateDraft: 'Construiește-mi o aplicație care',
    appsIdeasTitle: 'Începeți de la o idee',
    appsIdeasIntro:
      'Fiecare deschide un chat nou cu aplicația descrisă. Ajustați-o, apoi trimiteți-o.',
    appsIdeaPrices: 'Monitorizarea prețurilor concurenței',
    appsIdeaPricesDraft:
      'Construiește-mi o aplicație care urmărește prețurile concurenților pentru produsele mele și arată ce s-a schimbat față de săptămâna trecută.',
    appsIdeaStock: 'Planificator de stoc și reaprovizionare',
    appsIdeaStockDraft:
      'Construiește-mi o aplicație care arată stocul pe produs, semnalează ce se termină în două săptămâni și propune cantități de reaprovizionare.',
    appsIdeaCompare: 'Comparație de produse',
    appsIdeaCompareDraft:
      'Construiește-mi o aplicație care compară produsele unul lângă altul după preț, specificații și recenzii.',
    appsIdeaReturns: 'Evidența retururilor',
    appsIdeaReturnsDraft:
      'Construiește-mi o aplicație în care echipa mea înregistrează retururile cu fotografii și urmărește fiecare rambursare până la închidere.',
    appsNewHeading: 'O aplicație, făcută pentru munca dvs.',
    appsNewIntro: 'Descrieți de ce aveți nevoie. Aplicația prinde contur lângă conversație.',
    appsNewChip: 'Se creează o aplicație nouă',
    appsNewChipRemove: 'Opriți crearea unei aplicații noi',
    appsNewCanvasTitle: 'Ce ar trebui să facă această aplicație?',
    appsNewCanvasBody: 'Descrieți-o în chat. Aplicația prinde contur aici.',
    appsOpening: 'Se deschide {app}…',
    appsOpeningUnnamed: 'Se deschide aplicația…',
    appsCannotOpen: '{app} nu se poate deschide acum',
    appsCannotOpenUnnamed: 'Această aplicație nu se poate deschide acum',
    appsCannotOpenBody: 'Ceva a împiedicat încărcarea ei.',
  },
  sk: {
    appsSearch: 'Hľadať aplikácie',
    appsDescription:
      'Nástroje vytvorené pre to, ako pracujete. Otvárajte, zdieľajte a vylepšujte aplikácie svojej firmy.',
    appsCreateInChat: 'Vytvoriť v chate',
    appsEmptyTitle: 'Začnite nástrojom, ktorý váš tím potrebuje.',
    appsEmptyBody:
      'Opíšte ho v chate. Aplikácia vzniká vedľa konverzácie a zostane súkromná, kým ju nezdieľate.',
    appsCreateFirst: 'Vytvoriť prvú aplikáciu',
    appsCreateDraft: 'Vytvor mi aplikáciu, ktorá',
    appsIdeasTitle: 'Začnite nápadom',
    appsIdeasIntro: 'Každý otvorí nový chat s opísanou aplikáciou. Upravte ho a odošlite.',
    appsIdeaPrices: 'Sledovanie cien konkurencie',
    appsIdeaPricesDraft:
      'Vytvor mi aplikáciu, ktorá sleduje ceny konkurencie pri mojich produktoch a ukazuje, čo sa zmenilo od minulého týždňa.',
    appsIdeaStock: 'Plánovač zásob a objednávok',
    appsIdeaStockDraft:
      'Vytvor mi aplikáciu, ktorá ukazuje zásoby podľa produktu, označí, čo sa do dvoch týždňov minie, a navrhne množstvá na objednanie.',
    appsIdeaCompare: 'Porovnanie produktov',
    appsIdeaCompareDraft:
      'Vytvor mi aplikáciu, ktorá porovná produkty vedľa seba podľa ceny, parametrov a recenzií.',
    appsIdeaReturns: 'Evidencia vratiek',
    appsIdeaReturnsDraft:
      'Vytvor mi aplikáciu, v ktorej môj tím zapisuje vratky s fotkami a sleduje každé vrátenie peňazí až do vybavenia.',
    appsNewHeading: 'Aplikácia vytvorená pre vašu prácu.',
    appsNewIntro: 'Opíšte, čo potrebujete. Aplikácia vzniká vedľa konverzácie.',
    appsNewChip: 'Tvorba novej aplikácie',
    appsNewChipRemove: 'Ukončiť tvorbu novej aplikácie',
    appsNewCanvasTitle: 'Čo má táto aplikácia robiť?',
    appsNewCanvasBody: 'Opíšte to v chate. Aplikácia vznikne tu.',
    appsOpening: 'Otvára sa {app}…',
    appsOpeningUnnamed: 'Otvára sa aplikácia…',
    appsCannotOpen: '{app} sa teraz nedá otvoriť',
    appsCannotOpenUnnamed: 'Túto aplikáciu sa teraz nedá otvoriť',
    appsCannotOpenBody: 'Niečo zabránilo jej načítaniu.',
  },
  sl: {
    appsSearch: 'Išči aplikacije',
    appsDescription:
      'Orodja, narejena za vaš način dela. Odpirajte, delite in izboljšujte aplikacije svojega podjetja.',
    appsCreateInChat: 'Ustvarite v klepetu',
    appsEmptyTitle: 'Začnite z orodjem, ki ga potrebuje vaša ekipa.',
    appsEmptyBody:
      'Opišite ga v klepetu. Aplikacija nastaja ob pogovoru in ostane zasebna, dokler je ne delite.',
    appsCreateFirst: 'Ustvarite prvo aplikacijo',
    appsCreateDraft: 'Ustvari mi aplikacijo, ki',
    appsIdeasTitle: 'Začnite z zamislijo',
    appsIdeasIntro: 'Vsaka odpre nov klepet z opisano aplikacijo. Prilagodite jo in pošljite.',
    appsIdeaPrices: 'Spremljanje cen konkurence',
    appsIdeaPricesDraft:
      'Ustvari mi aplikacijo, ki spremlja cene konkurence za moje izdelke in pokaže, kaj se je spremenilo od prejšnjega tedna.',
    appsIdeaStock: 'Načrtovalnik zalog in naročil',
    appsIdeaStockDraft:
      'Ustvari mi aplikacijo, ki prikaže zaloge po izdelkih, označi, kar bo pošlo v dveh tednih, in predlaga količine za naročilo.',
    appsIdeaCompare: 'Primerjava izdelkov',
    appsIdeaCompareDraft:
      'Ustvari mi aplikacijo, ki primerja izdelke enega ob drugem po ceni, specifikacijah in mnenjih.',
    appsIdeaReturns: 'Sledenje vračilom',
    appsIdeaReturnsDraft:
      'Ustvari mi aplikacijo, v kateri moja ekipa beleži vračila s fotografijami in spremlja vsako vračilo denarja do zaključka.',
    appsNewHeading: 'Aplikacija, narejena za vaše delo.',
    appsNewIntro: 'Opišite, kaj potrebujete. Aplikacija nastaja ob pogovoru.',
    appsNewChip: 'Ustvarjanje nove aplikacije',
    appsNewChipRemove: 'Prekini ustvarjanje nove aplikacije',
    appsNewCanvasTitle: 'Kaj naj počne ta aplikacija?',
    appsNewCanvasBody: 'Opišite to v klepetu. Aplikacija nastaja tukaj.',
    appsOpening: 'Odpiranje aplikacije {app}…',
    appsOpeningUnnamed: 'Odpiranje aplikacije…',
    appsCannotOpen: '{app} se trenutno ne more odpreti',
    appsCannotOpenUnnamed: 'Te aplikacije trenutno ni mogoče odpreti',
    appsCannotOpenBody: 'Nekaj je preprečilo nalaganje.',
  },
  sv: {
    appsSearch: 'Sök appar',
    appsDescription:
      'Verktyg gjorda för hur ni arbetar. Öppna, dela och förbättra ert företags appar.',
    appsCreateInChat: 'Skapa i chatten',
    appsEmptyTitle: 'Börja med ett verktyg som ditt team behöver.',
    appsEmptyBody:
      'Beskriv det i chatten. Din app tar form bredvid samtalet och förblir privat tills du delar den.',
    appsCreateFirst: 'Skapa din första app',
    appsCreateDraft: 'Bygg en app åt mig som',
    appsIdeasTitle: 'Börja från en idé',
    appsIdeasIntro: 'Varje idé öppnar en ny chatt med appen beskriven. Ändra den och skicka.',
    appsIdeaPrices: 'Prisbevakning av konkurrenter',
    appsIdeaPricesDraft:
      'Bygg en app åt mig som bevakar konkurrenternas priser på mina produkter och visar vad som har ändrats sedan förra veckan.',
    appsIdeaStock: 'Lager- och beställningsplanerare',
    appsIdeaStockDraft:
      'Bygg en app åt mig som visar lagersaldo per produkt, flaggar det som tar slut inom två veckor och föreslår beställningsantal.',
    appsIdeaCompare: 'Produktjämförelse',
    appsIdeaCompareDraft:
      'Bygg en app åt mig som jämför produkter sida vid sida på pris, specifikationer och recensioner.',
    appsIdeaReturns: 'Returhantering',
    appsIdeaReturnsDraft:
      'Bygg en app åt mig där mitt team registrerar returer med bilder och följer varje återbetalning tills den är avslutad.',
    appsNewHeading: 'En app, gjord för ditt arbete.',
    appsNewIntro: 'Beskriv vad du behöver. Din app tar form bredvid samtalet.',
    appsNewChip: 'Bygger en ny app',
    appsNewChipRemove: 'Sluta bygga en ny app',
    appsNewCanvasTitle: 'Vad ska den här appen göra?',
    appsNewCanvasBody: 'Beskriv det i chatten. Din app tar form här.',
    appsOpening: 'Öppnar {app}…',
    appsOpeningUnnamed: 'Öppnar din app…',
    appsCannotOpen: '{app} kan inte öppnas just nu',
    appsCannotOpenUnnamed: 'Den här appen kan inte öppnas just nu',
    appsCannotOpenBody: 'Något hindrade den från att läsas in.',
  },
};
export default appsNavCopy;
